import { MinusCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { Input } from "antd-v5";
import AppLoader from "components/AppLoader";
import ConfirmPopup from "components/ConfirmPopup";
import message from "components/CustomMessage";
import { PermissionSetMap } from "constants/RBACConstants";
import { TICKET_SYSTEM_TYPE_DISPLAY_TEXT } from "constants/TicketContants";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import { isEmpty, isString, uniqBy } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";
import { hasPermission } from "utils/authService";
import ModalUtil from "utils/modalUtil";
import { CellRenderers, Sorters } from "utils/tableUtils";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACDropdown from "ac-components/ACDropdown";
import ACTable, { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACBadge from "ac-components/ac-badge/ACBadge";
import ACButton from "ac-components/ac-button/ACButton";
import CheckIcon from "ac-components/ac-icons/CheckIcon";
import FilterIcon from "ac-components/ac-icons/FilterIcon";
import MenuIcon from "ac-components/ac-icons/MenuIcon";
import SearchIcon from "ac-components/ac-icons/SearchIcon";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";

import { getTicketStatus, isTicketCreationFailed } from "../utils";
import { useFindingDetails } from "./FindingDetailsProvider";

interface TicketDetails {
  armorcodeTicketId: number;
  ticketDetailJpaDto: {
    ticketId: string;
    latestStatus: string;
    url: string;
  };
  createdAt: number;
  armorcodeTicketStatus: string;
  status: string;
  latestStatus: string;
  ticketSystem: string;
}
interface Finding {
  armorcodeTicket: string;
  id: number;
  testType: string;
}
interface Bucket {
  addFindings: (ids: number[], findings: Finding[]) => void;
}
interface UseFindingDetailsReturn {
  id: string;
  ticketDetails: TicketDetails[];
  fetchTickets: () => void;
  finding: Finding;
  history: { push: (path: string) => void };
  bucket: Bucket;
  loading: boolean;
}

export default function FindingDetailsTicket() {
  const { id, ticketDetails, fetchTickets, finding, history, bucket, loading } =
    useFindingDetails() as unknown as UseFindingDetailsReturn;
  const [searchText, setSearchText] = useState("");
  const [selectedValue, setSelectedValue] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState(ticketDetails);
  const onSearch = useCallback((e) => {
    setSearchText(e.target.value || "");
  }, []);

  useEffect(() => {
    setFilteredData(ticketDetails);
    setSearchText("");
    setSelectedValue([]);
  }, [ticketDetails]);

  const searchMethod = useCallback((value, list) => {
    value = value.trim().toLowerCase();
    if (value.length === 0) {
      return list;
    }
    return list.filter((item: TicketDetails) => {
      const ticketID = item.ticketDetailJpaDto.ticketId?.toLowerCase();
      const ticketURL = item.ticketDetailJpaDto?.url.toLowerCase();
      return ticketID?.includes(value) || ticketURL === value;
    });
  }, []);

  const detachTicket = useCallback(
    (data) => {
      if (data?.armorcodeTicketId) {
        ModalUtil.show({
          content: (
            <ConfirmPopup
              //@ts-expect-error
              title={`Are you sure want to detach ticket ?`}
              okText="Yes"
              note
              noteLength={1000}
              onOk={async (note: any) => {
                await TicketsCreationAPI.detachJiraTicketForFinding({
                  findingId: [id],
                  note,
                  ticketId: [data.ticketDetailJpaDto.ticketId],
                }).promise.then(() => {
                  message.success("Ticket detachment done successfully.");
                });
              }}
            />
          ),
          afterClose: fetchTickets,
        });
      }
    },
    [id, fetchTickets],
  );

  const onActionClick = useCallback(
    (key, record) => {
      switch (key) {
        case "detachTicket":
          detachTicket(record);
          break;
        default:
          break;
      }
    },
    [detachTicket],
  );

  const setFindingsAndRedirect = (path: string) => {
    bucket.addFindings([finding.id], [finding]);
    history.push(path);
  };

  const isFindingSourceJira = finding?.testType === "JIRA";
  const isCreateTicketDisabled =
    !hasPermission(PermissionSetMap.CREATE_TICKETS) || isFindingSourceJira;

  const actionRenderer = useCallback(
    (record: TicketDetails) => {
      const isDetachTicketDisabled = isTicketCreationFailed(
        record.status || record.latestStatus,
      );
      const items = [
        {
          value: "detachTicket",
          label: "Detach Ticket",
          icon: <MinusCircleOutlined />,
          permission: PermissionSetMap.DELETE_TICKETS,
          disabled: isDetachTicketDisabled,
        },
      ];
      return (
        <ACDropdown
          items={items}
          onOptionClick={(key) => onActionClick(key, record)}
          placement="bottomRight"
        >
          <MenuIcon />
        </ACDropdown>
      );
    },
    [onActionClick],
  );

  const columns = useMemo<ACTableColumnType<TicketDetails>[]>(() => {
    return [
      {
        title: "ID",
        dataIndex: "armorcodeTicketId",
        key: "armorcodeTicketId",
        width: 200,
        render: (value, record) => (
          <div className="d-flex justify-content-between align-items-center">
            {getTicketStatus(record)}
            <a
              href={
                record.ticketDetailJpaDto?.url
                  ? record.ticketDetailJpaDto?.url
                  : isString(record.armorcodeTicketId)
                    ? record.armorcodeTicketId
                    : ""
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <ACButton
                size="sm"
                type="outline"
                disabled={
                  !record.ticketDetailJpaDto?.url &&
                  !isString(record.armorcodeTicketId)
                }
              >
                View in{" "}
                {CellRenderers.VALUE_OR_NA(
                  TICKET_SYSTEM_TYPE_DISPLAY_TEXT[
                    record.ticketSystem as keyof typeof TICKET_SYSTEM_TYPE_DISPLAY_TEXT
                  ],
                )}
              </ACButton>
            </a>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "ticketDetailJpaDto",
        key: "ticketDetailJpaDto",
        width: 120,
        render: (value, record) => {
          if (
            record.ticketDetailJpaDto?.latestStatus ||
            record.armorcodeTicketStatus
          ) {
            const status =
              record.ticketDetailJpaDto?.latestStatus ||
              record.armorcodeTicketStatus;
            return (
              <ACTagColored
                label={
                  record.ticketDetailJpaDto?.latestStatus ||
                  record.armorcodeTicketStatus
                }
                color={status === "To Do" ? "Neutral" : "Success"}
              />
            );
          }
          return CellRenderers.VALUE_OR_NA();
        },
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 130,
        render: (date) => moment(date).format("MMMM DD, YYYY"),
      },
    ];
  }, []);

  const items = uniqBy(
    ticketDetails?.map((item) => ({
      label:
        item.ticketDetailJpaDto?.latestStatus || item.armorcodeTicketStatus,
      value:
        item.ticketDetailJpaDto?.latestStatus || item.armorcodeTicketStatus,
    })),
    "value",
  ).sort(Sorters.STRING("value"));

  const onOptionClick = (value: any) => {
    setSelectedValue((prev) => {
      const updatedValues = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      const newFilteredData =
        updatedValues.length > 0
          ? ticketDetails.filter((item: TicketDetails) =>
              updatedValues.includes(
                item.ticketDetailJpaDto?.latestStatus ||
                  item.armorcodeTicketStatus,
              ),
            )
          : ticketDetails;
      setFilteredData(newFilteredData);
      return updatedValues;
    });
  };

  const filteredList = searchMethod(searchText, filteredData);

  const { columns: customizedColumns } = useTableCustomizationHelper({
    columns,
    prefKey: "finding-details-ticket-table",
    skipColumnVisibility: true,
    draggable: false,
  });

  if (loading || !finding) {
    return <AppLoader />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center m-b-l">
        <div>
          <Input
            autoFocus
            prefix={<SearchIcon />}
            onChange={onSearch}
            allowClear
            placeholder="Search tickets"
          />
        </div>
        <ACButtonGroup size="md">
          <ACDropdown
            items={items.map((item) => {
              return {
                ...item,
                secondaryIcon: selectedValue.includes(item.value) ? (
                  <CheckIcon />
                ) : (
                  ""
                ),
              };
            })}
            onOptionClick={onOptionClick}
          >
            <ACButtonGroupItem disabled={isEmpty(ticketDetails)}>
              <FilterIcon />
              {!isEmpty(selectedValue) ? (
                <ACBadge type="beta" content={selectedValue.length} />
              ) : null}
            </ACButtonGroupItem>
          </ACDropdown>
          <Tooltip
            title={
              isFindingSourceJira
                ? "Tickets cannot be created for a finding when the source tool is a ticketing system."
                : ""
            }
          >
            <span>
              <ACButtonGroupItem
                disabled={loading || isCreateTicketDisabled}
                onClick={() => setFindingsAndRedirect("/findings/createTicket")}
              >
                Create Ticket
              </ACButtonGroupItem>
            </span>
          </Tooltip>
        </ACButtonGroup>
      </div>
      <ACTable<TicketDetails>
        dataSource={filteredList}
        columns={customizedColumns}
        loading={loading}
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
        computeTableWidth
        autoEnableScroll
        fullPage
        actionRender={actionRenderer}
        actionColumnWidth={60}
      />
    </div>
  );
}
